<template>
	<!-- The district's tag vocabulary, embedded as a section of the Organization
	     settings page (the standalone Tags page was removed): a tag is district
	     configuration, so it lives beside the rest of it rather than in a nav
	     entry of its own. The symbol library those tags draw with stays on the
	     Symbology page; the member's own view of them is under Preferences. -->
	<div>
		<div
			class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4"
		>
			<div>
				<p class="text-body-secondary mb-0">
					The district's tag vocabulary: each tag is defined once, coloured once
					and applied to many Sites. The order below is the order the map ranks
					by — a Site carrying several tags draws with the highest one that has
					a symbol bound to it.
				</p>
			</div>
			<div class="d-flex align-items-center gap-3">
				<span class="text-body-secondary small text-nowrap">
					<strong>{{ liveCount }} of {{ TAG_CAP }} tags</strong>
					<!-- Retired tags are outside the count, so "retire one to make
					     room" reads off the number rather than out of a paragraph. -->
					<span v-if="retiredCount > 0" class="tag-retired-count">
						· {{ retiredCount }} retired, not counted
					</span>
				</span>
				<ButtonLoading
					text="New tag"
					icon="bi bi-plus-lg"
					variant="primary"
					:disabled="capReached"
					:title="capReached ? capSentence : 'Add a tag to the district'"
					@click="openNew"
				/>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- The new-tag form: a panel rather than a modal, because the table it
		     feeds stays in view while the district names the tag. -->
		<div v-if="showNew" class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-3">New tag</h2>
				<div class="row g-3">
					<div class="col-12 col-md-4">
						<label class="form-label" for="tag-name">Name</label>
						<input
							id="tag-name"
							v-model="form.name"
							class="form-control"
							placeholder="Invasive Aedes zone"
							@keyup.enter="create"
						/>
					</div>
					<div class="col-12 col-md-4">
						<label class="form-label" for="tag-priority">Priority</label>
						<input
							id="tag-priority"
							v-model.number="form.priority"
							class="form-control"
							type="number"
							step="1"
							title="Higher ranks above lower: it is both this list's order and the map's ranking"
						/>
					</div>
					<div class="col-12 col-md-2">
						<label class="form-label" for="tag-color">Colour</label>
						<input
							id="tag-color"
							v-model="form.color"
							class="form-control form-control-color"
							type="color"
							title="The tag's identity colour: its chip, swatch and legend entry"
						/>
					</div>
					<div class="col-12">
						<label class="form-label" for="tag-description">
							Description
						</label>
						<input
							id="tag-description"
							v-model="form.description"
							class="form-control"
							placeholder="What this tag means, in the district's words"
						/>
					</div>
					<div class="col-12">
						<span class="form-label d-block">Icon (optional)</span>
						<IconUpload v-model="form.iconUuid" />
					</div>
				</div>
				<p class="form-text">
					The tag's colour is its identity in the ledger and the picker; the map
					colour comes from the symbol bound to it, which this screen seeds from
					this colour. Retire a tag to make room: retired tags are free against
					the {{ TAG_CAP }}-tag limit.
				</p>
				<div class="d-flex gap-2">
					<ButtonLoading
						text="Create tag"
						icon="bi bi-check-lg"
						variant="primary"
						:loading="creating"
						@click="create"
					/>
					<ButtonLoading
						text="Cancel"
						variant="outline-secondary"
						:disabled="creating"
						@click="closeNew"
					/>
				</div>
			</div>
		</div>

		<div class="card">
			<div
				class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2 pb-0"
			>
				<h2 class="h5 mb-0">Vocabulary</h2>
				<div class="form-check form-switch mb-0">
					<input
						id="show-retired"
						v-model="showRetired"
						class="form-check-input"
						type="checkbox"
					/>
					<label class="form-check-label small" for="show-retired">
						Show retired
					</label>
				</div>
			</div>
			<div class="card-body">
				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="visibleTags.length === 0"
					class="text-center py-4 text-muted"
				>
					No tags yet. A tag names what a Site is or what it is for — create the
					district's first one above.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-hover align-middle mb-0">
						<thead class="table-light">
							<tr>
								<th scope="col" class="tag-handle-col">
									<span class="visually-hidden">Reorder</span>
								</th>
								<th scope="col">
									<span class="visually-hidden">Colour</span>
								</th>
								<th scope="col">
									<span class="visually-hidden">Icon</span>
								</th>
								<th scope="col">Name</th>
								<th scope="col">Slug</th>
								<th scope="col">Description</th>
								<th scope="col" class="text-end">Priority</th>
								<th scope="col" class="text-end">Used by</th>
								<th scope="col">Retired</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="(tag, index) in visibleTags" :key="tag.id">
								<tr v-if="isRetiredGroupStart(index)" class="table-light">
									<td colspan="10" class="small text-body-secondary py-1">
										Retired — out of the picker, still on the Sites that carry
										them, and free against the limit.
									</td>
								</tr>
								<tr
									:class="{ 'table-active': dragId === tag.id }"
									@dragover.prevent
									@drop.prevent.stop="dropOn(tag)"
								>
									<td class="tag-handle-col">
										<span
											v-if="!tag.retired"
											class="tag-handle"
											draggable="true"
											title="Drag onto another tag to set the district's order"
											@dragstart="dragId = tag.id"
											@dragend="dragId = null"
										>
											<i class="bi bi-grip-vertical"></i>
										</span>
										<span
											v-else
											class="text-body-secondary"
											title="Retired tags are outside the order"
											>—</span
										>
									</td>
									<td>
										<span
											class="tag-swatch"
											:style="{ backgroundColor: tag.color }"
											:title="tag.color"
										></span>
									</td>
									<td>
										<img
											v-if="tagIcon(tag)"
											:src="tagIcon(tag) || ''"
											class="tag-icon"
											:alt="tag.name"
										/>
									</td>
									<td>
										<RouterLink
											:to="`/_/configuration/tags/${tag.id}`"
											class="fw-semibold text-decoration-none"
										>
											{{ tag.name }}
										</RouterLink>
									</td>
									<td>
										<code>{{ tag.slug }}</code>
									</td>
									<td class="small text-body-secondary">
										{{ tag.description || "—" }}
									</td>
									<td class="text-end">{{ tag.priority }}</td>
									<td class="text-end text-nowrap">
										{{ (tag.site_count ?? 0).toLocaleString() }} Sites
									</td>
									<td>
										<template v-if="tag.retired">
											<span class="badge text-bg-secondary">Retired</span>
											<span
												v-if="tag.retired_at"
												class="small text-body-secondary ms-1"
											>
												<TimeRelativeWithTooltip :time="tag.retired_at" />
											</span>
										</template>
										<span v-else class="text-body-secondary small">—</span>
									</td>
									<td class="text-end text-nowrap">
										<div class="d-inline-flex gap-1">
											<RouterLink :to="`/_/configuration/tags/${tag.id}`">
												<button
													type="button"
													class="btn btn-sm btn-outline-primary"
												>
													Edit
												</button>
											</RouterLink>
											<!-- Tags are retired, never deleted: a tag on ten
											     thousand Sites is a fact about those Sites. -->
											<ButtonLoading
												v-if="!tag.retired"
												text="Retire"
												variant="outline-secondary"
												size="sm"
												title="Take this tag out of the picker; every assignment it has is kept"
												:loading="busyId === tag.id"
												@click="setRetired(tag, true)"
											/>
											<ButtonLoading
												v-else
												text="Restore"
												variant="outline-secondary"
												size="sm"
												title="Put this tag back in the picker"
												:loading="busyId === tag.id"
												@click="setRetired(tag, false)"
											/>
										</div>
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			v-model:show="toast.show"
			:title="toast.title"
			:message="toast.message"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import IconUpload from "@/components/symbology/IconUpload.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { symbolIconURL } from "@/lib/symbology";
import type { SiteTag } from "@/type/api";

/** The number of live tags a district may hold (§7.5). Restated here only so the
 * budget is visible before it is a 409: the server owns the rule, and its own
 * sentence is what a refusal toasts. */
const TAG_CAP = 200;

const tags = ref<SiteTag[]>([]);
const loading = ref(true);
const error = ref("");
const showRetired = ref(false);
const showNew = ref(false);
const creating = ref(false);
const busyId = ref<number | null>(null);
const dragId = ref<number | null>(null);
const reordering = ref(false);
const toast = reactive({ show: false, title: "", message: "" });

const form = reactive({
	name: "",
	description: "",
	color: "#2563eb",
	iconUuid: null as string | null,
	priority: 0,
});

const liveTags = computed(() => tags.value.filter((tag) => !tag.retired));
const retiredTags = computed(() => tags.value.filter((tag) => tag.retired));
const liveCount = computed(() => liveTags.value.length);
const retiredCount = computed(() => retiredTags.value.length);
const capReached = computed(() => liveCount.value >= TAG_CAP);
/** One sentence, used as the disabled button's title and as the toast a submit
 * that got past it shows — so the reason and the refusal read the same. */
const capSentence = computed(
	() =>
		`This district already holds ${liveCount.value} live tags (the limit is ${TAG_CAP}); retire a tag to make room.`,
);
const visibleTags = computed(() =>
	showRetired.value
		? [...liveTags.value, ...retiredTags.value]
		: liveTags.value,
);

function isRetiredGroupStart(index: number): boolean {
	return (
		showRetired.value &&
		retiredCount.value > 0 &&
		index === liveTags.value.length
	);
}

function tagIcon(tag: SiteTag): string | null {
	return symbolIconURL(tag.icon_uuid);
}

function showToast(title: string, message: string) {
	toast.title = title;
	toast.message = message;
	toast.show = true;
}

function errorMessage(e: any): string {
	return e?.response?.data?.message || e?.message || "The request failed.";
}

async function load() {
	if (tags.value.length === 0) loading.value = true;
	error.value = "";
	try {
		tags.value = await apiClient.JSONGet<SiteTag[]>(
			"/api/site-tag?include_retired=1",
		);
	} catch (e: any) {
		error.value = errorMessage(e);
	} finally {
		loading.value = false;
	}
}

function openNew() {
	if (capReached.value) {
		showToast("Tag limit", capSentence.value);
		return;
	}
	showNew.value = true;
}

function closeNew() {
	showNew.value = false;
	resetForm();
}

function resetForm() {
	form.name = "";
	form.description = "";
	form.color = "#2563eb";
	form.iconUuid = null;
	form.priority = 0;
}

async function create() {
	if (creating.value) return;
	// A stale page still showing the form must not be able to slip past the cap:
	// the server refuses it either way, and the sentence is the same one the
	// button's title states.
	if (capReached.value) {
		showToast("Tag limit", capSentence.value);
		return;
	}
	const name = form.name.trim();
	if (name === "") {
		showToast("Name required", "A tag needs a name.");
		return;
	}
	creating.value = true;
	try {
		await apiClient.JSONPost<SiteTag>("/api/site-tag", {
			name,
			description: form.description,
			color: form.color,
			icon_uuid: form.iconUuid ?? "",
			priority: form.priority,
		});
		showNew.value = false;
		resetForm();
		showToast("Tag created", `“${name}” is in the district's vocabulary.`);
		await load();
	} catch (e: any) {
		showToast("Could not create the tag", errorMessage(e));
	} finally {
		creating.value = false;
	}
}

async function setRetired(tag: SiteTag, retire: boolean) {
	if (busyId.value !== null) return;
	busyId.value = tag.id;
	try {
		await apiClient.JSONPost(
			`/api/site-tag/${tag.id}/${retire ? "retire" : "restore"}`,
		);
		showToast(
			retire ? "Tag retired" : "Tag restored",
			retire
				? `“${tag.name}” is out of the picker; the Sites that carry it keep it.`
				: `“${tag.name}” is back in the picker.`,
		);
		await load();
	} catch (e: any) {
		showToast(
			retire ? "Could not retire the tag" : "Could not restore the tag",
			errorMessage(e),
		);
	} finally {
		busyId.value = null;
	}
}

/** A drop states the district's whole live order: the reorder body must name
 * exactly the live tag set, so retired rows are never part of it. */
async function dropOn(target: SiteTag) {
	const moving = dragId.value;
	dragId.value = null;
	if (moving === null || reordering.value || target.retired) return;
	const order = liveTags.value.map((tag) => tag.id);
	const from = order.indexOf(moving);
	const to = order.indexOf(target.id);
	if (from < 0 || to < 0 || from === to) return;
	order.splice(from, 1);
	order.splice(to, 0, moving);
	// Reorder the local list first, so the row follows the drop; the reload
	// after the write brings the priorities the server stored.
	const byId = new Map(tags.value.map((tag) => [tag.id, tag]));
	tags.value = [
		...order.map((id) => byId.get(id) as SiteTag),
		...retiredTags.value,
	];
	reordering.value = true;
	try {
		await apiClient.JSONPost("/api/site-tag/reorder", { order });
		await load();
	} catch (e: any) {
		showToast("Could not save the order", errorMessage(e));
		await load();
	} finally {
		reordering.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.tag-handle-col {
	width: 1.5rem;
}

.tag-handle {
	cursor: grab;
	color: var(--bs-secondary-color);
}

.tag-swatch {
	display: inline-block;
	width: 1rem;
	height: 1rem;
	border-radius: 50%;
	border: 1px solid var(--bs-secondary-border-subtle);
}

.tag-icon {
	width: 20px;
	height: 20px;
	object-fit: contain;
}

.tag-retired-count {
	color: var(--bs-secondary-color);
}
</style>
